import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { gridConfig } from '../../fuse-responsive-grid/src/grid';
import { ChevronButton } from './chevron-button';
import PaginationButton from './pagination-button';
import { OlStyled, SrPoliteText, StyledNav } from './styled-pagination';
import useMediaQuery from './useMediaQuery';
import {
	MAX_BUTTONS_DESKTOP,
	MAX_BUTTONS_MOBILE,
	NEXT_PAGE,
	PREVIOUS_PAGE,
	generateButtonArray,
} from './utils';

const propTypes = {
	currentPage: PropTypes.number.isRequired,
	numberOfPages: PropTypes.number.isRequired,
	callback: PropTypes.func,
	dataTestId: PropTypes.string,
	className: PropTypes.string,
	seoUrl: PropTypes.string,
};

const defaultProps = {
	callback: undefined,
	dataTestId: undefined,
	className: undefined,
	seoUrl: undefined,
};

function Pagination({
	currentPage,
	numberOfPages,
	callback,
	dataTestId,
	className,
	seoUrl,
	...props
}) {
	const isMobile = useMediaQuery(`(max-width: ${gridConfig.s.maxWidth})`);
	let maxButtons = isMobile ? MAX_BUTTONS_MOBILE : MAX_BUTTONS_DESKTOP;
	const maxPages = numberOfPages > currentPage ? numberOfPages : currentPage;
	
	/* Change number of buttons if device is rotated */
	useEffect(() => {
		maxButtons = isMobile ? MAX_BUTTONS_MOBILE : MAX_BUTTONS_DESKTOP;
	}, [isMobile]);

	const [pages, setPages] = useState([]);
	const [pageUrls, setPageUrls] = useState(() => generateButtonArray({
            currentPage,
            maxPages,
            maxButtons,
            isMobile,
            seoUrl,
        }));
	const { prevPageUrl, nextPageUrl } = pageUrls;

    useEffect(() => {
        const { pages: newPages, prevPageUrl: newPrevPageUrl, nextPageUrl: newNextPageUrl } =
            generateButtonArray({
                currentPage,
                maxPages,
                maxButtons,
                isMobile,
                seoUrl,
            });

        setPages(newPages);
       	setPageUrls({ prevPageUrl: newPrevPageUrl, nextPageUrl: newNextPageUrl });
    }, [currentPage, isMobile, maxPages, maxButtons, seoUrl]); 

	return (
		<StyledNav
			aria-label="Pagination"
			className={className}
			data-testid={dataTestId || 'fuse-pagination-data-testid'}
			{...props}
		>
			<ChevronButton
				currentPage={currentPage}
				label={PREVIOUS_PAGE}
				callback={callback}
				numberOfPages={maxPages}
				dataTestId={dataTestId}
				seoHref={prevPageUrl}
			/>
			<OlStyled>
				{pages && pages.map(({ label, href }) => (
					<PaginationButton
						key={`Page-${label}`}
						text={label}
						currentPage={currentPage}
						callback={callback}
						dataTestId={dataTestId}
						seoHref={seoUrl && href}
					/>
				))}
			</OlStyled>
			<ChevronButton
				currentPage={currentPage}
				label={NEXT_PAGE}
				callback={callback}
				numberOfPages={maxPages}
				dataTestId={dataTestId}
				seoHref={nextPageUrl}
			/>
			<SrPoliteText>{`Page ${currentPage} selected`}</SrPoliteText>
		</StyledNav>
	);
}

Pagination.propTypes = propTypes;
Pagination.defaultProps = defaultProps;
Pagination.displayName = 'Pagination';

export { Pagination };

export default Pagination;
